import { useEffect } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    ufoEventSignupFormLoad,
    ufoEventSiteNameFormLoad,
    Bundle,
    Edition,
    getPrimaryProductKeyFromBundle
} from "@/components/signup/utils";
import TryIntent from "@/components/try-intent/src";
import { useUserContext } from "@/components/user-context";
import { BgColors } from "@/libraries/design/components/link/src/link";

import { useAnalyticsClient } from "../../../../analytics/wac-analytics/analytics-client.tsx";

import {
    FormLoading,
    SignupForm,
    SignupFormSuccessUFOEvent,
    SiteNameForm
} from "./components";
import { SignInProviders } from "./components/signup-form/components/index.ts";
import { SignupFormContextProvider } from "./components/signup-form/hooks";

const SignupModule = ({
    bundle,
    edition,
    isNewUser,
    emailFormOnly,
    animatedSocialBanner,
    hasRebrandedSignupFormStyles,
    bgColor = "bgWhite",
    customSignInProviders,
    hideLegalText
}: {
    bundle: Bundle;
    edition: Edition;
    isNewUser?: boolean;
    emailFormOnly?: boolean;
    animatedSocialBanner?: boolean;
    hasRebrandedSignupFormStyles?: boolean;
    bgColor?: BgColors;
    customSignInProviders?: SignInProviders;
    hideLegalText?: boolean;
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { profile, user } = useUserContext();
    const analyticsClient = useAnalyticsClient();

    const showLoadingSpinner =
        user.requestInProgress ||
        (user.isUserLoggedIn && profile.requestInProgress);

    useEffect(() => {
        ufoEventSiteNameFormLoad.start({ bundle, edition });
        ufoEventSignupFormLoad.start({ bundle, edition });
    }, [bundle, edition]);

    useEffect(() => {
        if (
            !analyticsClient ||
            profile.requestInProgress ||
            user.requestInProgress
        ) {
            return;
        }
        if (user.isUserLoggedIn) {
            createAnalyticsEvent({
                eventType: "ui",
                action: "viewed",
                actionSubject: "siteNameComponent",
                containerId: "bxp-ui-signup",
                actionSubjectId: "standalone",
                attributes: {
                    productKey: getPrimaryProductKeyFromBundle(bundle),
                    edition,
                    userType: profile.cohorts?.userType,
                    domainType: profile.cohorts?.domainType,
                    companyType: profile.cohorts?.companyType
                }
            }).fire();
        } else {
            createAnalyticsEvent({
                eventType: "ui",
                action: "viewed",
                actionSubject: "accountCreationComponent",
                containerId: "bxp-ui-signup",
                actionSubjectId: emailFormOnly ? "embedded" : "standalone",
                attributes: {
                    productKey: getPrimaryProductKeyFromBundle(bundle),
                    edition
                }
            }).fire();
        }
    }, [
        createAnalyticsEvent,
        analyticsClient,
        user.isUserLoggedIn,
        user.requestInProgress,
        bundle,
        edition,
        emailFormOnly,
        profile.requestInProgress,
        profile.cohorts?.userType,
        profile.cohorts?.domainType,
        profile.cohorts?.companyType
    ]);

    return (
        <SignupFormContextProvider bundle={bundle} edition={edition}>
            <div data-testid="signup-module">
                <div aria-busy={user.requestInProgress} aria-live="polite">
                    {showLoadingSpinner ? (
                        <FormLoading />
                    ) : !user.isUserLoggedIn ? (
                        <>
                            <TryIntent
                                intentType="form"
                                productKey={getPrimaryProductKeyFromBundle(
                                    bundle
                                )}
                                edition={edition}
                            >
                                <SignupForm
                                    emailFormOnly={emailFormOnly}
                                    animatedSocialBanner={animatedSocialBanner}
                                    hasRebrandedSignupFormStyles={
                                        hasRebrandedSignupFormStyles
                                    }
                                    bgColor={bgColor}
                                    customSignInProviders={
                                        customSignInProviders
                                    }
                                    hideLegalText={hideLegalText}
                                />
                                <SignupFormSuccessUFOEvent />
                            </TryIntent>
                        </>
                    ) : (
                        <TryIntent
                            intentType="form"
                            productKey={getPrimaryProductKeyFromBundle(bundle)}
                            edition={edition}
                        >
                            <SiteNameForm
                                isNewUser={isNewUser}
                                hasRebrandedSignupFormStyles={
                                    hasRebrandedSignupFormStyles
                                }
                                bgColor={bgColor}
                            />
                        </TryIntent>
                    )}
                </div>
            </div>
        </SignupFormContextProvider>
    );
};

export default SignupModule;
